/**
 * 公开分享落地页用的接口（无登录）。
 *
 * 与 directorApi 的差异：
 * - 不发送 Authorization header
 * - 失败响应同样返回 { error }，但 status 码语义略不同（401=密码错；429=锁定/限流；404=链接失效）
 */

import { API_BASE } from '../../../services/api';

export class ShareApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ShareApiError';
    this.status = status;
    this.code = code;
  }
}

async function publicFetch<T = unknown>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };
  const response = await fetch(`${API_BASE}${path}`, { ...options, headers });

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    /* ignore */
  }

  if (!response.ok) {
    const err = body as { error?: string; code?: string } | null;
    throw new ShareApiError(
      err?.error || `请求失败 (${response.status})`,
      response.status,
      err?.code
    );
  }

  const ok = body as { ok?: boolean; data?: T };
  if (ok && typeof ok === 'object' && 'data' in ok) {
    return ok.data as T;
  }
  return body as T;
}

// ============================================================================
// 类型
// ============================================================================

export interface ShareLandingInfo {
  projectName: string;
  projectDescription: string | null;
  authorName: string | null;
  compositionDuration: number | null;
  requiresPassword: boolean;
  allowDownload: boolean;
  viewCount: number;
}

export interface ShareVideoUrl {
  /** 分享自己的流式播放地址（可能带短时播放票据） */
  videoUrl: string;
  /** 允许下载时的流式下载地址；禁止下载为 null */
  downloadUrl: string | null;
  allowDownload: boolean;
}

// ============================================================================
// API
// ============================================================================

export const directorShareApi = {
  /** 获取分享落地页元信息 */
  getShareInfo(token: string) {
    return publicFetch<ShareLandingInfo>(`/director/share/${token}`);
  },

  /** 验证密码 */
  verifyPassword(token: string, password: string) {
    return publicFetch<void>(`/director/share/${token}/verify-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  },

  /** 获取视频播放/下载 URL */
  getVideoUrl(token: string, password?: string) {
    const query = password ? `?password=${encodeURIComponent(password)}` : '';
    return publicFetch<ShareVideoUrl>(`/director/share/${token}/video${query}`);
  },
};
